// BlogView.tsx
import React, { useEffect, useState } from 'react';
import { BlogPost, ViewState, UserProfile, PILLAR_META, CLUSTER_META, ContentPillar } from '../types';
import { getBlogPosts, getBlogPost } from '../services/firebase';
import { ArrowLeft, Clock, Calendar, User, Loader, Search, Share2, Play } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { MarkdownRenderer } from './ui/MarkdownRenderer';
import { SEO } from './SEO';
import {
  BlogPostSchema, BreadcrumbSchema, BlogSchema, VideoSchema,
  FAQSchema, HowToSchema,
} from './StructuredData';
import { StickyTOC } from './StickyTOC';
import { ROICalculator } from './ROICalculator';
import { PILLAR_FAQ, PILLAR_HOWTO_UCP, PILLAR_HOWTO_ACP, PILLAR_HOWTO_INTENT } from '../data/PillarPageData';

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const getCategoryDefaultImage = (category?: string) => {
  const n = category?.toLowerCase() || '';
  if (n.includes('technical')) return '/og/engineering.png';
  if (n.includes('gateway'))   return '/og/public.png';
  if (n.includes('product'))   return '/og/product.png';
  if (n.includes('founder'))   return '/og/philosophy.png';
  return '/og-image.png';
};

const getAbsoluteUrl = (url: string) =>
  url.startsWith('http') ? url : `https://fivedollarworkday.com${url.startsWith('/') ? '' : '/'}${url}`;

const slugify = (text: string) =>
  text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();

// ─── Types ────────────────────────────────────────────────────────────────────

interface BlogViewProps {
  viewState: 'BLOG' | 'BLOG_POST';
  postId: string | null;
  onNavigate: (view: ViewState, id?: string) => void;
  user: UserProfile | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const BlogView: React.FC<BlogViewProps> = ({ viewState, postId, onNavigate }) => {
  const [posts, setPosts]               = useState<BlogPost[]>([]);
  const [currentPost, setCurrentPost]   = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading]           = useState(true);
  const [searchTerm, setSearchTerm]     = useState('');
  const [selectedPillar, setSelectedPillar] = useState<ContentPillar | null>(null);

  useEffect(() => {
    if (viewState === 'BLOG') {
      setLoading(true);
      getBlogPosts().then(data => {
        setPosts([...data].sort((a, b) =>
          new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
        ));
        setLoading(false);
      });
    }
  }, [viewState]);

  useEffect(() => {
    if (viewState === 'BLOG_POST' && postId) {
      setLoading(true);
      getBlogPost(postId).then(post => {
        setCurrentPost(post);
        getBlogPosts().then(all => {        });
        setLoading(false);
      });
    }
  }, [viewState, postId]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  // ─── Single Post View ─────────────────────────────────────────────────────────
  if (viewState === 'BLOG_POST' && currentPost) {
    const image     = getAbsoluteUrl(currentPost.coverImageUrl || getCategoryDefaultImage(currentPost.category));
    const slug      = currentPost.slug || slugify(currentPost.title);
    const canonical = currentPost.canonicalUrl || `https://fivedollarworkday.com/blog/${slug}`;

    const twitterShareUrl  = `https://twitter.com/intent/tweet?text=${encodeURIComponent(currentPost.title)}&url=${encodeURIComponent(canonical)}&via=FiveWorkday`;
    const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonical)}`;

    // AFTER — schema-correct: a pillar page is any post with a contentPillar
    // set and no contentCluster, per types.ts's own definition ("Omit on the
    // pillar page itself — it contains all clusters as sections") and per
    // AdminDashboard.tsx's own UI instruction ("— No cluster (this is the
    // pillar page) —"). This is the actual rule the admin form is built
    // around; BlogView.tsx just never checked it.
    const isPillarPage =
  !!currentPost.contentPillar && !currentPost.contentCluster;

    const cqFaqItems = currentPost.canonicalQuestions
      ? Object.entries(currentPost.canonicalQuestions).map(([, question]) => ({
          question,
          answer: currentPost.excerpt || currentPost.title,
        }))
      : [];

    const hasCQSchema = cqFaqItems.length > 0;

    return (
      <>
        <SEO
          title={currentPost.title}
          description={currentPost.excerpt}
          canonical={canonical}
          ogType={currentPost.youtubeId ? 'video.other' : 'article'}
          ogImage={
            currentPost.youtubeId
              ? `https://img.youtube.com/vi/${currentPost.youtubeId}/maxresdefault.jpg`
              : image
          }
          article={{
            publishedTime: currentPost.createdAt,
            modifiedTime:  currentPost.updatedAt,
            author:        currentPost.authorName || 'Jeremy Mears',
            section:       currentPost.category,
            tags:          currentPost.tags || [],
          }}
        />

        <BlogPostSchema
          headline={currentPost.title}
          description={currentPost.excerpt}
          datePublished={currentPost.createdAt || new Date().toISOString()}
          dateModified={currentPost.updatedAt || currentPost.createdAt || new Date().toISOString()}
          authorName={currentPost.authorName || 'Jeremy Mears'}
          authorUrl="https://fivedollarworkday.com/about"
          image={image}
          url={canonical}
          keywords={currentPost.tags || []}
          articleSection={currentPost.category}
        />

        <BreadcrumbSchema
          items={[
            { name: 'Home',      url: 'https://fivedollarworkday.com' },
            { name: 'Blog', url: 'https://fivedollarworkday.com/blog' },
            { name: currentPost.category || 'Post', url: `https://fivedollarworkday.com/blog?category=${currentPost.category}` },
            { name: currentPost.title, url: canonical },
          ]}
        />

        {hasCQSchema && <FAQSchema items={cqFaqItems} />}

        {isPillarPage && (
          <>
            {!hasCQSchema && <FAQSchema items={PILLAR_FAQ} />}
            <HowToSchema
              name={PILLAR_HOWTO_UCP.name}
              description={PILLAR_HOWTO_UCP.description}
              steps={PILLAR_HOWTO_UCP.steps}
              totalTime={PILLAR_HOWTO_UCP.totalTime}
            />
            <HowToSchema
              name={PILLAR_HOWTO_ACP.name}
              description={PILLAR_HOWTO_ACP.description}
              steps={PILLAR_HOWTO_ACP.steps}
              totalTime={PILLAR_HOWTO_ACP.totalTime}
            />
            <HowToSchema
              name={PILLAR_HOWTO_INTENT.name}
              description={PILLAR_HOWTO_INTENT.description}
              steps={PILLAR_HOWTO_INTENT.steps}
              totalTime={PILLAR_HOWTO_INTENT.totalTime}
            />
          </>
        )}

        {isPillarPage && <StickyTOC content={currentPost.content} />}

        <article className={`mx-auto px-6 py-24 bg-background ${isPillarPage ? 'max-w-4xl xl:max-w-3xl' : 'max-w-3xl'}`}>

          <Button
            render={<a href="/blog" onClick={(e: React.MouseEvent) => { e.preventDefault(); onNavigate('BLOG'); }} />}
            variant="ghost"
            className="h-auto px-0 py-0 mb-12 text-sm font-medium text-muted-foreground hover:text-primary hover:bg-transparent group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Blog
          </Button>

          <header className="mb-12">
            <div className="flex items-center gap-6 text-sm text-muted-foreground font-mono mb-6 uppercase tracking-wider">
              <span className="text-primary font-bold">{currentPost.category || 'General'}</span>
              <span className="flex items-center">
                <Calendar className="w-3 h-3 mr-2" />
                {new Date(currentPost.createdAt || '').toLocaleDateString()}
              </span>
              <span className="flex items-center">
                <Clock className="w-3 h-3 mr-2" />
                {currentPost.readTime}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-foreground mb-8 tracking-tight">
              {currentPost.title}
            </h1>

            <div className="flex items-center gap-4 mb-8">
              <span className="text-xs text-muted-foreground font-mono uppercase tracking-widest flex items-center gap-2">
                <Share2 className="w-3 h-3" /> Share
              </span>
              <Button
                render={<a href={twitterShareUrl} target="_blank" rel="noopener noreferrer" />}
                variant="link"
                size="sm"
                className="h-auto p-0 text-foreground hover:text-primary"
              >
                X (Twitter)
              </Button>
              <Button
                render={<a href={linkedinShareUrl} target="_blank" rel="noopener noreferrer" />}
                variant="link"
                size="sm"
                className="h-auto p-0 text-foreground hover:text-primary"
              >
                LinkedIn
              </Button>
            </div>
          </header>

          {currentPost.youtubeId ? (
            <div className="mb-12 rounded-2xl overflow-hidden shadow-soft border border-border aspect-video relative group">
              <VideoSchema
                name={currentPost.title}
                description={currentPost.excerpt}
                uploadDate={currentPost.createdAt || new Date().toISOString()}
                thumbnailUrl={`https://img.youtube.com/vi/${currentPost.youtubeId}/maxresdefault.jpg`}
                embedUrl={`https://www.youtube.com/embed/${currentPost.youtubeId}`}
              />
              <iframe
                width="100%" height="100%"
                src={`https://www.youtube.com/embed/${currentPost.youtubeId}?rel=0`}
                title={currentPost.title} frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen className="absolute inset-0"
              />
            </div>
          ) : (
            <img
              src={image} alt={currentPost.title}
              className="w-full rounded-2xl mb-16 shadow-soft border border-border"
              loading="eager"
            />
          )}

          <Card className="flex flex-col sm:flex-row sm:items-center gap-6 mb-12 border-border py-0">
            <CardContent className="flex flex-col sm:flex-row sm:items-center gap-6 p-6 w-full">
              <div className="flex-1">
                <span className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                  Narrative Series
                </span>
                <span className="font-medium text-foreground text-lg">
                  {currentPost.playlist 
                    ? PILLAR_META[currentPost.playlist]?.label || currentPost.category 
                    : currentPost.category}
                </span>
              </div>
              <div className="sm:border-l sm:border-border sm:pl-6">
                <span className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                  Format
                </span>
                <span className="font-medium text-foreground flex items-center gap-2">
                  {currentPost.youtubeId
                    ? <><div className="w-2 h-2 bg-destructive rounded-full animate-pulse" /> Watch ({currentPost.readTime})</>
                    : `Read (${currentPost.readTime})`}
                </span>
              </div>
              <div className="sm:border-l sm:border-border sm:pl-6">
                <span className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                  Pillar
                </span>
                <span className="font-medium text-foreground">
                  {currentPost.contentPillar 
                    ? PILLAR_META[currentPost.contentPillar]?.label || currentPost.category 
                    : currentPost.category}
                </span>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center gap-4 mb-16 pb-16 border-b border-border">
            <div className="w-12 h-12 rounded-full bg-secondary border border-border flex items-center justify-center overflow-hidden">
              {currentPost.authorPhoto
                ? <img src={currentPost.authorPhoto} alt={currentPost.authorName} className="w-full h-full object-cover" />
                : <User className="w-5 h-5 text-muted-foreground" />}
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">{currentPost.authorName || 'Jeremy Mears'}</p>
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-wide">Five Dollar Workday</p>
            </div>
          </div>

          <div className="prose prose-lg prose-slate prose-headings:font-medium prose-a:text-primary hover:prose-a:text-primary/80">
            <MarkdownRenderer content={currentPost.content} />
          </div>

          {isPillarPage && <ROICalculator />}

          {isPillarPage && (
            <div className="mt-16 p-8 bg-secondary rounded-2xl border border-border text-center">
              <h3 className="text-2xl font-bold text-foreground mb-3">Ready to get started?</h3>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
                Whether you're a merchant, agent builder, or just curious — there's a next step for you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  render={<a href="https://agenticcommerce.web.app" target="_blank" rel="noopener noreferrer" />}
                  variant="primary"
                  size="lg"
                  className="rounded-full h-auto px-6 py-3"
                >
                  Connect your store →
                </Button>
                <Button
                  render={<a href="mailto:hello@fivedollarworkday.com" />}
                  variant="outline"
                  size="lg"
                  className="rounded-full h-auto px-6 py-3"
                >
                  Get in touch
                </Button>
              </div>
            </div>
          )}

          {relatedPosts.length > 0 && (
            <section className="mt-24 pt-16 border-t border-border">
              <h2 className="text-2xl font-medium text-foreground mb-10">Related in this Series</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedPosts.map(post => {
                  const relatedHref = `/blog/${post.slug || post.id}`;
                  return (
                    <a
                      key={post.id} href={relatedHref}
                      onClick={e => { e.preventDefault(); onNavigate('BLOG_POST', post.slug || post.id); }}
                      className="group block"
                    >
                      <Card className="border-border hover:shadow-md hover:border-primary/30 transition-all">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-2 mb-3">
                            <p className="text-xs uppercase text-primary font-bold tracking-wider">{post.category}</p>
                            {post.youtubeId && (
                              <Play className="w-3 h-3 text-muted-foreground fill-current" />
                            )}
                          </div>
                          <h3 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                        </CardContent>
                      </Card>
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          <div className="mt-24 pt-12 border-t border-border text-center">
            <p className="text-foreground/80 mb-6 text-lg font-light">
              What we're actually finding, as we find it.
            </p>
            <div className="flex justify-center gap-8">
              <Button
                render={<a href="https://x.com/JeremyMearsX" target="_blank" rel="noopener noreferrer" />}
                variant="link"
                className="h-auto p-0 text-foreground font-medium hover:text-primary"
              >
                @JeremyMears
              </Button>
              <Button
                render={<a href="https://x.com/FiveWorkday" target="_blank" rel="noopener noreferrer" />}
                variant="link"
                className="h-auto p-0 text-foreground font-medium hover:text-primary"
              >
                @FiveWorkday
              </Button>
            </div>
          </div>

        </article>
      </>
    );
  }

  // ─── Blog Index View ──────────────────────────────────────────────────────────

  const pillars       = Object.keys(PILLAR_META) as ContentPillar[];
  const filteredPosts = posts.filter(p => {
    const s = searchTerm.toLowerCase();
    return (
      (p.title.toLowerCase().includes(s) || p.excerpt.toLowerCase().includes(s)) &&
      (selectedPillar ? p.contentPillar === selectedPillar : true)
    );
  });

  return (
    <>
      <SEO
        title="Build Log"
        description="Services as Software."
        canonical="https://fivedollarworkday.com/blog"
      />

      <BlogSchema
        name="Five Dollar Workday"
        description="What have we shipped?"
        url="https://fivedollarworkday.com/blog"
        posts={filteredPosts.slice(0, 10).map(post => ({
          headline:      post.title,
          url:           `https://fivedollarworkday.com/blog/${post.slug || post.id}`,
          datePublished: post.createdAt || new Date().toISOString(),
          author:        post.authorName || 'Jeremy Mears',
        }))}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Home',      url: 'https://fivedollarworkday.com' },
          { name: 'Build Log', url: 'https://fivedollarworkday.com/blog' },
        ]}
      />

      <section className="max-w-7xl mx-auto px-6 py-24 bg-background min-h-screen">
        <header className="mb-20 text-center max-w-3xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-semibold tracking-tight text-foreground mb-6">Build Log</h1>
          <p className="text-xl text-muted-foreground font-light leading-relaxed">
            What we're actually finding, as we find it.<br className="hidden md:block" />
          </p>
        </header>

        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-16 border-b border-border pb-8">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder="Search the log..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-12 rounded-full"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto no-scrollbar">
            <Badge
              onClick={() => setSelectedPillar(null)}
              variant={!selectedPillar ? 'default' : 'outline'}
              className="cursor-pointer px-6 py-2 rounded-full text-sm font-medium"
            >
              All
            </Badge>
            {pillars.map(pillarKey => (
              <Badge
                key={pillarKey}
                onClick={() => setSelectedPillar(pillarKey)}
                variant={selectedPillar === pillarKey ? 'default' : 'outline'}
                className="cursor-pointer px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap"
              >
                {PILLAR_META[pillarKey].label}
              </Badge>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {filteredPosts.map(post => {
            const href = `/blog/${post.slug || post.id}`;
            return (
              <a
                key={post.id}
                href={href}
                onClick={e => { e.preventDefault(); onNavigate('BLOG_POST', post.slug || post.id); }}
                className="group block"
              >
                <Card className="border-transparent hover:border-border hover:shadow-lg transition-all duration-500">
                  <CardContent className="p-8 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <p className="text-xs uppercase text-primary font-bold tracking-widest">
                        {post.contentPillar 
                          ? PILLAR_META[post.contentPillar]?.label || post.category 
                          : post.category}
                      </p>
                      <span className="text-xs text-muted-foreground font-mono">{post.readTime}</span>
                    </div>
                    <h2 className="text-2xl font-medium text-foreground mb-4 group-hover:text-primary transition-colors leading-tight">
                      {post.title}
                    </h2>
                    <p className="text-muted-foreground mb-6 line-clamp-3 flex-grow font-light leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm font-medium text-foreground mt-auto pt-6 border-t border-border group-hover:border-transparent transition-colors">
                      <span className="flex items-center gap-2">
                        {post.youtubeId && <Play className="w-3.5 h-3.5 text-muted-foreground fill-current" />}
                        {post.contentCluster && (
                      <span className="text-xs text-muted-foreground font-mono uppercase tracking-wide">
                        {CLUSTER_META[post.contentCluster]?.label || post.contentCluster.replace(/-/g, ' ')}
                      </span>
                    )}
                      </span>
                      <span className="flex items-center group-hover:translate-x-1 transition-transform">
                        Read entry <span className="ml-2">→</span>
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </a>
            );
          })}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-32">
            <p className="text-muted-foreground text-lg">No entries found matching your criteria.</p>
            <Button
              variant="link"
              onClick={() => { setSearchTerm(''); setSelectedPillar(null); }}
              className="mt-4 h-auto p-0 text-primary"
            >
              Clear filters
            </Button>
          </div>
        )}
      </section>
    </>
  );
};